import { useState, useEffect } from "react";
import axios from "axios";
import { getAuthHeaders } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInquiries = () => {
    setLoading(true);
    axios.get(`${API}/admin/inquiries`, { headers: getAuthHeaders(), withCredentials: true })
      .then(res => setInquiries(res.data))
      .catch(() => toast.error("Failed to load inquiries"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchInquiries(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Inquiry deleted");
      fetchInquiries();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-inquiries" className="px-6 md:px-12 lg:px-24 py-8">
      <div className="mb-10">
        <h1 className="font-['DM_Serif_Display'] text-3xl sm:text-4xl text-[#1A1A2E] mb-1">Inquiries</h1>
        <p className="text-[#94A3B8] text-sm">{inquiries.length} total inquiries</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#32A334] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#64748B]">No inquiries yet.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="inquiries-list">
          {inquiries.map((inq) => (
            <div key={inq.id} className="bg-white border border-[#E2E8F0] p-6 rounded-lg" data-testid={`inquiry-${inq.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <h3 className="text-[#1A1A2E] font-medium">{inq.name}</h3>
                    {inq.property_title && (
                      <span className="text-xs px-2 py-0.5 border border-[#32A334]/30 text-[#32A334] bg-[#32A334]/5 rounded">
                        {inq.property_title}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-[#94A3B8] mb-3 flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {inq.email}</span>
                    {inq.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {inq.phone}</span>}
                    {inq.created_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(inq.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-[#64748B] text-sm leading-relaxed">{inq.message}</p>
                </div>
                <button
                  onClick={() => handleDelete(inq.id)}
                  data-testid={`delete-inquiry-${inq.id}`}
                  className="text-[#CBD5E1] hover:text-[#D94E4E] transition-colors p-1 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
